// components/Paywall.tsx — out-of-turns / buy-turns modal.
//
// Triggered by:
//   - askEngine() throwing OutOfTurnsError (402 from /api/turn)
//   - the "Buy turns" link in the user-chip menu
//   - StoryCapReachedError or ScenarioLockedError (multi-story gating)
//
// Layout:
//   - Non-subscriber: the subscription pitch is the primary path. A small
//     "or just this once" topup link sits underneath. The subscription
//     benefit list is the centrepiece.
//   - Subscriber: hierarchy flips. Top-up is primary; a small "manage
//     subscription" link sits underneath.
//
// The `reason` prop is purely cosmetic — it changes the eyebrow + title
// only. The benefits + CTAs stay identical between trigger contexts.
//
// Below the paid CTAs there is an admin-gated "Request free extension
// (testing)" path. It is hidden entirely when the app is running on any
// emberhollow.ai domain (production) — the state machine + renderer stay
// in the file, just gated behind `!isProductionDomain`. The server-side
// env gate (`EXTENSION_REQUESTS_ENABLED`) is unchanged; this change is
// purely about client-side surface visibility on production. The
// `onExtensionGranted` prop is preserved on the component signature so
// App.tsx doesn't need editing, but it will never fire on production.

import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";
import {
  startCheckout,
  openBillingPortal,
  requestExtension as apiRequestExtension,
  redeemExtension as apiRedeemExtension,
  type BillingSubscription,
} from "../services/billingApi";

// Hard-coded prices, intentionally NOT in the i18n bundle. The numeric
// values are billing contract — they ride server-side in setupStripe — and
// localising the format breaks the SKU pinning. Interpolated into the
// translated CTA strings so the price token is the only literal in the
// rendered text.
const MONTHLY_PRICE_LABEL = "USD$7.99";
const TOPUP_PRICE_LABEL = "USD$4.99";

/**
 * Render the "Currently at N turns." line as JSX with the count rendered
 * in an inline span so we can style it differently from the surrounding
 * text. The translation key uses an `<strong>` tag around the count for
 * machine-translator clarity; this helper splits the resolved string on
 * the tag and renders the count inside an inline span.
 */
function renderCurrentTurnsLine(
  t: (key: string, opts?: Record<string, unknown>) => string,
  balance: number,
): JSX.Element {
  const raw = t("paywall:subscriber.currentTurns", { count: balance });
  // Tag-and-split on `<strong>{balance}</strong>` — the translator preserves
  // the literal tags. Falls back gracefully when the tag is missing.
  const open = "<strong>";
  const close = "</strong>";
  const a = raw.indexOf(open);
  const b = a >= 0 ? raw.indexOf(close, a + open.length) : -1;
  if (a < 0 || b < 0) {
    return <>{raw}</>;
  }
  const before = raw.slice(0, a);
  const after = raw.slice(b + close.length);
  return (
    <>
      {before}
      <span style={{ color: "#e8c896", fontStyle: "normal" }}>{balance}</span>
      {after}
    </>
  );
}

/** Trigger context. Adjusts the eyebrow + title only — CTAs/benefits unchanged. */
export type PaywallReason =
  | "out_of_turns"
  | "manual"
  | "story_cap"
  /**
   * Free user opened a story whose scenarioId doesn't match their
   * forever_default_scenario_id. The Paywall renders on top of the game
   * UI (which stays scrollable in the background) and the close button is
   * dismissable so the user can keep reading. See STORIES_CONTRACT §1
   * and the App.tsx integration ("in-game paywall overlay for locked
   * stories").
   */
  | "story_locked";

export interface PaywallProps {
  /** Current subscription, if any. Drives subscriber vs non-subscriber layout. */
  subscription: BillingSubscription | null;
  /** Current turn balance (used for the subscriber top-up framing). */
  balance?: number | null;
  /** What triggered the paywall — purely cosmetic copy variant. */
  reason?: PaywallReason;
  /** Close handler — triggered by the X button or backdrop click. */
  onClose: () => void;
  /** Re-fetch billing status after the user comes back from Stripe / paid elsewhere. */
  onRefreshStatus: () => Promise<void> | void;
  /**
   * Fired after a successful extension redemption (admin-gated testing path).
   * App.tsx wires this to `refreshBilling()` + a toast. Never fires on the
   * emberhollow.ai production domain — the entire extension CTA is hidden
   * there.
   */
  onExtensionGranted?: () => void;
}

// Stop propagation helper for the modal card so backdrop clicks close,
// but card clicks don't.
const STOP: (e: React.MouseEvent) => void = (e) => e.stopPropagation();

type ExtensionMode = "idle" | "code-entry";

// Detect emberhollow.ai (and any subdomain). Returns true on:
//   emberhollow.ai
//   www.emberhollow.ai
//   beta.emberhollow.ai
// Returns false on:
//   localhost
//   127.0.0.1
//   ember-hollow.fly.dev
//   anything else
// SSR-safe (window check).
const isProductionDomain =
  typeof window !== "undefined" &&
  /(^|\.)emberhollow\.ai$/i.test(window.location.hostname);

export default function Paywall({
  subscription,
  balance,
  reason,
  onClose,
  onRefreshStatus,
  onExtensionGranted,
}: PaywallProps) {
  const { t } = useTranslation(["paywall", "common"]);
  const [loading, setLoading] = useState<
    | null
    | "subscription"
    | "topup"
    | "portal"
    | "refresh"
    | "ext-request"
    | "ext-redeem"
  >(null);
  const [error, setError] = useState<string | null>(null);

  // Extension flow state.
  const [extensionMode, setExtensionMode] = useState<ExtensionMode>("idle");
  const [extensionCode, setExtensionCode] = useState("");
  const [extensionError, setExtensionError] = useState<string | null>(null);

  // Subscriber-vs-not. Trialing, active, past_due all count as "subscriber"
  // for paywall purposes — past_due users are still entitled to the
  // discounted top-up tier; Stripe handles dunning.
  const isSubscriber =
    subscription !== null &&
    subscription.status !== "canceled" &&
    subscription.status !== "unpaid";

  async function go(kind: "subscription" | "topup") {
    if (loading) return;
    setLoading(kind);
    setError(null);
    try {
      const { url } = await startCheckout(kind);
      if (!url) throw new Error("No checkout URL returned");
      window.location.href = url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setLoading(null);
    }
  }

  async function goPortal() {
    if (loading) return;
    setLoading("portal");
    setError(null);
    try {
      const { url } = await openBillingPortal();
      if (!url) throw new Error("No portal URL returned");
      window.location.href = url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setLoading(null);
    }
  }

  async function refresh() {
    if (loading) return;
    setLoading("refresh");
    setError(null);
    try {
      await onRefreshStatus();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(null);
    }
  }

  async function startExtensionRequest() {
    if (loading) return;
    setLoading("ext-request");
    setExtensionError(null);
    try {
      await apiRequestExtension();
      setExtensionMode("code-entry");
      setExtensionCode("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setExtensionError(msg);
    } finally {
      setLoading(null);
    }
  }

  function cancelExtension() {
    if (loading === "ext-redeem" || loading === "ext-request") return;
    setExtensionMode("idle");
    setExtensionCode("");
    setExtensionError(null);
  }

  async function submitExtensionCode() {
    if (loading) return;
    const trimmed = extensionCode.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setExtensionError(t("paywall:extension.codeRequired"));
      return;
    }
    setLoading("ext-redeem");
    setExtensionError(null);
    try {
      await apiRedeemExtension(trimmed);
      // Success: close the Paywall, signal upstream so App.tsx can refresh
      // billing + toast.
      onExtensionGranted?.();
      onClose();
    } catch (err) {
      const e = err as Error & { body?: unknown };
      let msg = e.message || t("paywall:extension.redemptionFailed");
      // If the server returned attemptsRemaining (on invalid_code), append
      // the count so the user sees it inline.
      const body = e.body as { attemptsRemaining?: number } | null;
      if (body && typeof body.attemptsRemaining === "number") {
        msg = t("paywall:extension.attemptsRemaining", {
          message: msg,
          count: body.attemptsRemaining,
        });
      }
      setExtensionError(msg);
    } finally {
      setLoading(null);
    }
  }

  const dimmed: CSSProperties = loading
    ? { opacity: 0.55, cursor: "default" }
    : {};

  // ── Inline styles new to this rewrite ─────────────────────────────────
  // The Paywall's broad gothic shell + colour palette stays in styles.ts
  // (modalCard, modalCtaPrimary, modalEyebrow, ...). Everything below is
  // specific to the new layout and lives here so styles.ts stays untouched.

  const priceLine: CSSProperties = {
    margin: "0 0 6px",
    textAlign: "center" as const,
    color: "#e8c896",
    fontFamily: "'IM Fell English', Georgia, serif",
    fontSize: "1.55rem",
    lineHeight: 1.15,
    letterSpacing: "0.02em",
  };
  const priceTax: CSSProperties = {
    margin: "0 0 18px",
    textAlign: "center" as const,
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    letterSpacing: "0.08em",
  };
  const benefitList: CSSProperties = {
    listStyle: "none",
    margin: "0 0 22px",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  };
  const benefitRow: CSSProperties = {
    display: "flex",
    gap: 12,
    alignItems: "baseline",
  };
  const benefitGlyph: CSSProperties = {
    flexShrink: 0,
    color: "#d4a574",
    fontSize: "0.95rem",
    lineHeight: 1,
    width: "1em",
    textAlign: "center" as const,
    transform: "translateY(2px)",
  };
  const benefitTextWrap: CSSProperties = {
    flex: 1,
    minWidth: 0,
    lineHeight: 1.4,
  };
  const benefitPrimary: CSSProperties = {
    display: "block",
    color: "#e8d9b8",
    fontSize: "0.95rem",
    fontWeight: 600,
    letterSpacing: "0.005em",
  };
  const benefitSub: CSSProperties = {
    display: "block",
    marginTop: 2,
    color: "#8a7355",
    fontSize: "0.78rem",
    fontStyle: "italic",
    lineHeight: 1.45,
  };
  const dividerWrap: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "22px 0 16px",
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.68rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
  };
  const dividerLine: CSSProperties = {
    flex: 1,
    height: 1,
    background: "rgba(212,165,116,0.18)",
  };
  const secondaryPackLine: CSSProperties = {
    margin: "0 0 8px",
    textAlign: "center" as const,
    color: "#a89070",
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontStyle: "italic",
    fontSize: "0.92rem",
    lineHeight: 1.45,
  };
  const secondaryLinkBtn: CSSProperties = {
    display: "block",
    margin: "0 auto",
    background: "transparent",
    border: "none",
    color: "#d4a574",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    letterSpacing: "0.06em",
    padding: "6px 10px",
    cursor: "pointer",
    textDecoration: "underline",
  };
  const subBalanceLine: CSSProperties = {
    margin: "0 0 16px",
    textAlign: "center" as const,
    color: "#a89070",
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: "0.95rem",
    fontStyle: "italic",
  };
  const subAside: CSSProperties = {
    margin: "8px 0 0",
    textAlign: "center" as const,
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    letterSpacing: "0.04em",
    fontStyle: "normal" as const,
  };

  // ── Extension surface styles (unchanged) ──────────────────────────────
  const extensionWrap: CSSProperties = {
    marginTop: 18,
    paddingTop: 14,
    borderTop: "1px solid rgba(212,165,116,0.12)",
  };
  const extensionTinyBtn: CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    border: "1px dashed rgba(212,165,116,0.22)",
    color: "#8a7556",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.74rem",
    letterSpacing: "0.06em",
    padding: "9px 12px",
    cursor: "pointer",
    borderRadius: 2,
    transition: "all 0.2s",
    textAlign: "center" as const,
  };
  const extensionPanel: CSSProperties = {
    background: "rgba(212,165,116,0.05)",
    border: "1px solid rgba(212,165,116,0.18)",
    borderRadius: 2,
    padding: "12px 14px",
  };
  const extensionStatus: CSSProperties = {
    color: "#a89070",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.74rem",
    letterSpacing: "0.04em",
    margin: "0 0 10px",
    textAlign: "center" as const,
  };
  const extensionRow: CSSProperties = {
    display: "flex",
    gap: 8,
    alignItems: "stretch",
  };
  const extensionInput: CSSProperties = {
    flex: "1 1 auto",
    minWidth: 0,
    background: "rgba(5,3,2,0.5)",
    border: "1px solid rgba(212,165,116,0.3)",
    color: "#e8c896",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "1rem",
    letterSpacing: "0.3em",
    textAlign: "center" as const,
    padding: "8px 10px",
    borderRadius: 2,
    outline: "none",
  };
  const extensionRedeemBtn: CSSProperties = {
    flex: "0 0 auto",
    background: "rgba(212,165,116,0.18)",
    border: "1px solid rgba(212,165,116,0.5)",
    color: "#e8c896",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    letterSpacing: "0.06em",
    padding: "8px 14px",
    cursor: "pointer",
    borderRadius: 2,
  };
  const extensionCancelLink: CSSProperties = {
    background: "transparent",
    border: "none",
    color: "#7a6243",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    cursor: "pointer",
    textDecoration: "underline",
    padding: "8px 4px",
    letterSpacing: "0.06em",
  };
  const extensionInlineErr: CSSProperties = {
    marginTop: 10,
    color: "#c47a5a",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.72rem",
    textAlign: "center" as const,
    lineHeight: 1.5,
  };

  // ── Copy variants by reason ────────────────────────────────────────────
  // Eyebrow + title only. Benefits + CTAs are constant.
  let eyebrow: string;
  let title: string;
  if (isSubscriber) {
    if (reason === "out_of_turns") {
      eyebrow = t("paywall:eyebrows.outOfTurns");
      title = t("paywall:titles.outOfTurnsMember");
    } else if (reason === "story_cap") {
      eyebrow = t("paywall:eyebrows.storyCap");
      title = t("paywall:titles.storyCapMember");
    } else if (reason === "story_locked") {
      // Subscribers shouldn't normally hit `story_locked` — their
      // subscription unlocks every story — but copy is defensive in case
      // we ever cross-fire it from a stale snapshot.
      eyebrow = t("paywall:eyebrows.storyLocked");
      title = t("paywall:titles.storyLocked");
    } else {
      eyebrow = t("paywall:eyebrows.member");
      title = t("paywall:titles.topupMember");
    }
  } else {
    if (reason === "out_of_turns") {
      eyebrow = t("paywall:eyebrows.outOfTurns");
      title = t("paywall:titles.outOfTurnsFree");
    } else if (reason === "story_cap") {
      eyebrow = t("paywall:eyebrows.storyCap");
      title = t("paywall:titles.storyCapFree");
    } else if (reason === "story_locked") {
      eyebrow = t("paywall:eyebrows.storyLockedFree");
      title = t("paywall:titles.storyLocked");
    } else {
      eyebrow = t("paywall:eyebrows.keepersBargain");
      title = t("paywall:titles.membership");
    }
  }

  // ── Subscriber layout ─────────────────────────────────────────────────
  function renderSubscriberLayout() {
    return (
      <>
        {typeof balance === "number" && (
          <p style={subBalanceLine}>
            {renderCurrentTurnsLine(t, balance)}
          </p>
        )}

        {error && <div style={styles.modalErr}>{error}</div>}

        <button
          type="button"
          style={{ ...styles.modalCtaPrimary, ...dimmed }}
          onClick={() => go("topup")}
          disabled={loading !== null}
        >
          {loading === "topup"
            ? t("common:labels.redirecting")
            : t("paywall:subscriber.buyTopupCta", { price: TOPUP_PRICE_LABEL })}
          <span style={styles.modalCtaSubtitle}>{t("paywall:subscriber.topupSubtitle")}</span>
        </button>
        <p style={subAside}>{t("paywall:subscriber.memberPricingSoon")}</p>

        <div style={dividerWrap} aria-hidden="true">
          <span style={dividerLine} />
          <span>{t("common:labels.or")}</span>
          <span style={dividerLine} />
        </div>

        <button
          type="button"
          style={{ ...secondaryLinkBtn, ...dimmed }}
          onClick={goPortal}
          disabled={loading !== null}
        >
          {loading === "portal"
            ? t("paywall:subscriber.openingPortal")
            : t("paywall:subscriber.manageSubscription")}
        </button>
      </>
    );
  }

  // Subscribe-only mode: when the trigger is `story_locked`, the top-up
  // CTA + "or just once" divider are suppressed because turn packs don't
  // unlock new-scenario creation — only a subscription does. Surfacing
  // both options here would be misleading. A short italic note explains
  // the distinction near the bottom of the card.
  const isSubscribeOnly = reason === "story_locked";

  // Italic explanatory line shown only in subscribe-only mode.
  const storyLockedExplanation: CSSProperties = {
    margin: "16px 0 0",
    textAlign: "center" as const,
    color: "#8a7355",
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontStyle: "italic",
    fontSize: "0.88rem",
    lineHeight: 1.5,
  };

  // ── Non-subscriber layout ─────────────────────────────────────────────
  function renderNonSubscriberLayout() {
    return (
      <>
        <p style={priceLine}>
          {t("paywall:nonSubscriber.monthlyPrice", { price: MONTHLY_PRICE_LABEL })}
        </p>
        <p style={priceTax}>{t("paywall:nonSubscriber.plusTax")}</p>

        <ul style={benefitList}>
          <li style={benefitRow}>
            <span style={benefitGlyph} aria-hidden="true">✦</span>
            <span style={benefitTextWrap}>
              <span style={benefitPrimary}>{t("paywall:benefits.turnsPrimary")}</span>
              <span style={benefitSub}>{t("paywall:benefits.turnsSub")}</span>
            </span>
          </li>
          <li style={benefitRow}>
            <span style={benefitGlyph} aria-hidden="true">✦</span>
            <span style={benefitTextWrap}>
              <span style={benefitPrimary}>{t("paywall:benefits.savePrimary")}</span>
              <span style={benefitSub}>{t("paywall:benefits.saveSub")}</span>
            </span>
          </li>
          <li style={benefitRow}>
            <span style={benefitGlyph} aria-hidden="true">✦</span>
            <span style={benefitTextWrap}>
              <span style={benefitPrimary}>{t("paywall:benefits.storiesPrimary")}</span>
              <span style={benefitSub}>{t("paywall:benefits.storiesSub")}</span>
            </span>
          </li>
          <li style={benefitRow}>
            <span style={benefitGlyph} aria-hidden="true">✦</span>
            <span style={benefitTextWrap}>
              <span style={benefitPrimary}>{t("paywall:benefits.scenariosPrimary")}</span>
              <span style={{ ...benefitSub, fontStyle: "italic" }}>
                {t("paywall:benefits.scenariosSub").replace(/<\/?em>/g, "")}
              </span>
            </span>
          </li>
          <li style={benefitRow}>
            <span style={benefitGlyph} aria-hidden="true">✦</span>
            <span style={benefitTextWrap}>
              <span style={benefitPrimary}>{t("paywall:benefits.cancelPrimary")}</span>
              <span style={benefitSub}>{t("paywall:benefits.cancelSub")}</span>
            </span>
          </li>
        </ul>

        {error && <div style={styles.modalErr}>{error}</div>}

        <button
          type="button"
          style={{ ...styles.modalCtaPrimary, ...dimmed }}
          onClick={() => go("subscription")}
          disabled={loading !== null}
        >
          {loading === "subscription"
            ? t("common:labels.redirecting")
            : t("paywall:nonSubscriber.subscribeCta", { price: MONTHLY_PRICE_LABEL })}
        </button>

        {isSubscribeOnly ? (
          // story_locked variant: turn packs don't unlock new-scenario
          // creation, so we suppress the topup CTA entirely and explain
          // why. A short italic note sits where the divider used to.
          <p style={storyLockedExplanation}>
            {t("paywall:storyLockedExplanation")}
          </p>
        ) : (
          <>
            <div style={dividerWrap} aria-hidden="true">
              <span style={dividerLine} />
              <span>{t("paywall:nonSubscriber.orJustOnce")}</span>
              <span style={dividerLine} />
            </div>

            <p style={secondaryPackLine}>
              {t("paywall:nonSubscriber.secondaryPackLine", { price: TOPUP_PRICE_LABEL })}
            </p>
            <button
              type="button"
              style={{ ...secondaryLinkBtn, ...dimmed }}
              onClick={() => go("topup")}
              disabled={loading !== null}
            >
              {loading === "topup"
                ? t("common:labels.redirecting")
                : t("paywall:nonSubscriber.buyTurnPack")}
            </button>
          </>
        )}
      </>
    );
  }

  return (
    <div
      style={styles.modalBackdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
    >
      <div style={styles.modalCard} onClick={STOP}>
        <button
          type="button"
          aria-label={t("common:buttons.close")}
          style={styles.modalCloseBtn}
          onClick={onClose}
          disabled={loading !== null}
        >
          ×
        </button>

        <div style={styles.modalEyebrow}>{eyebrow}</div>
        <h2 id="paywall-title" style={styles.modalTitle}>
          {title}
        </h2>

        <div style={{ marginTop: 22 }}>
          {isSubscriber
            ? renderSubscriberLayout()
            : renderNonSubscriberLayout()}
        </div>

        <div style={styles.modalTertiaryRow}>
          <button
            type="button"
            style={styles.modalTertiaryLink}
            onClick={refresh}
            disabled={loading !== null}
          >
            {loading === "refresh"
              ? t("common:labels.checking")
              : t("paywall:refreshStatus")}
          </button>
        </div>

        {/* Extension request (admin-gated, env-flagged testing path).
            Hidden entirely on emberhollow.ai-family hostnames. */}
        {!isProductionDomain && (
          <div style={extensionWrap}>
            {extensionMode === "idle" ? (
              <button
                type="button"
                style={{ ...extensionTinyBtn, ...dimmed }}
                onClick={startExtensionRequest}
                disabled={loading !== null}
                aria-label={t("paywall:extension.ariaLabel")}
              >
                {loading === "ext-request"
                  ? t("paywall:extension.sending")
                  : t("paywall:extension.requestBtn")}
              </button>
            ) : (
              <div style={extensionPanel}>
                <p style={extensionStatus}>{t("paywall:extension.approvalSent")}</p>
                <div style={extensionRow}>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={extensionCode}
                    onChange={(e) => {
                      // Strip non-digits as the user types so the input is always
                      // visually consistent with the server's validator.
                      const digits = e.target.value
                        .replace(/\D+/g, "")
                        .slice(0, 6);
                      setExtensionCode(digits);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void submitExtensionCode();
                      }
                    }}
                    placeholder={t("paywall:extension.codePlaceholder")}
                    style={extensionInput}
                    disabled={loading === "ext-redeem"}
                    aria-label={t("paywall:extension.codeAriaLabel")}
                  />
                  <button
                    type="button"
                    style={{
                      ...extensionRedeemBtn,
                      ...(loading ? { opacity: 0.6, cursor: "default" } : {}),
                    }}
                    onClick={() => void submitExtensionCode()}
                    disabled={loading !== null || extensionCode.length !== 6}
                  >
                    {loading === "ext-redeem" ? "…" : t("paywall:extension.redeem")}
                  </button>
                </div>
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <button
                    type="button"
                    style={extensionCancelLink}
                    onClick={cancelExtension}
                    disabled={
                      loading === "ext-redeem" || loading === "ext-request"
                    }
                  >
                    {t("paywall:extension.cancel")}
                  </button>
                </div>
                {extensionError && (
                  <div style={extensionInlineErr}>{extensionError}</div>
                )}
              </div>
            )}
            {extensionMode === "idle" && extensionError && (
              <div style={extensionInlineErr}>{extensionError}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
